import type { SepaPayment } from '@api/hooks/types/createTransaction';
import type { Transaction } from '@api/hooks/types/getTransactions';
import type { Account } from '@api/hooks/useAccounts';
import { useCreateTransaction, useDocumentNumber } from '@api/hooks/useTransactions';
import { ControlledAccountList } from '@components/AccountList';
import Alert from '@components/alert';
import { ControlledTextInput } from '@components/ControlledTextInput';
import { DocumentNumberInput } from '@components/DocumentNumberInput';
import { FormBlocker } from '@components/FormBlocker';
import { InputDropdown } from '@components/input';
import { PermissionGate } from '@components/PermissionGate';
import TemplateFromPayment from '@components/templates/TemplateFromPayment';
import { PaymentTfa, SigningStatus } from '@components/tfa/PaymentTfa';
import VoPConfirmationBanner from '@components/VoPConfirmationBanner';
import { DATE_FORMAT } from '@constants';
import { useGeoDataContext } from '@contexts/GeoDataContext';
import {
	AlertType,
	Button,
	Collapsible,
	DateStringPicker,
	Typography,
} from '@gurupay/gp-ui-library';
import { useCreateTemplate } from '@hooks/useCreateTemplate';
import { usePaginatedAccounts } from '@hooks/usePaginatedAccounts';
import { PaymentDetailsSwitcher } from '@pages/templates/components';
import { prepareTemplate } from '@utils/utils';
import clsx from 'clsx';
import { addYears, format, startOfDay } from 'date-fns';
import { enUS, lt } from 'date-fns/locale';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { VOP_STATUS } from 'types/layout';
import { Capability } from 'types/roles';
import type { PaymentTemplate } from 'types/templates.ts';

import { AdditionalDataForm } from './components/AdditionalDataForm';
import { ReceiverAccountRender } from './components/ReceiverAccountRender';
import { useSepaValidation } from './hooks/useSepaValidation';

enum FormStatus {
	INITIAL = 'initial',
	CREATED = 'created',
	WARNING = 'warning',
}

export type SepaFormSchema = SepaPayment & { beneficiarySelf?: Account };

const vopMessageKey: Record<VOP_STATUS, string> = {
	NO_MATCH: 'vop.noMatch',
	CLOSE_MATCH: 'vop.closeMatch',
	MATCH: 'vop.match',
	CANNOT_VERIFY: 'vop.cannotVerify',
};

const vopStatusColor: Record<VOP_STATUS, string> = {
	NO_MATCH: 'text-status-error-500',
	CLOSE_MATCH: 'text-status-warning-500',
	MATCH: 'text-status-success-500',
	CANNOT_VERIFY: 'text-status-error-500',
};

const DEFAULT_VALUES = {
	currency: 'EUR',
	payment_urgency: 'NORMAL',
	execution_date: format(new Date(), DATE_FORMAT),
	beneficiary: {
		address: {
			type: 'STRUCTURED',
		},
	},
	ultimate_beneficiary: {
		address: {
			type: 'STRUCTURED',
		},
	},
	save_as_template: false,
} as Partial<SepaPayment>;

interface SepaPaymentFormProps {
	operation?: Transaction;
	selectedAccount?: Account;
	template?: PaymentTemplate;
}

const today = startOfDay(new Date());

// eslint-disable-next-line max-lines-per-function
export const SepaPaymentForm: FC<SepaPaymentFormProps> = ({
	operation,
	template,
	selectedAccount,
}) => {
	const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.INITIAL);
	const sepaPaymentForm = useForm<SepaFormSchema>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: DEFAULT_VALUES,
		values: {
			...DEFAULT_VALUES,
			...template,
			payment_type: 'DOMESTIC',
			document_number: '',
			...(operation && {
				// Currently an account id in payments is string in account it is number
				account_uuid: operation.account_uuid,
				amount: operation.amount,
				currency: operation.currency,
				payment_details: operation.payment_details,
				primary_payer: operation.primary_payer,
				payer: operation.payer,
				beneficiary: {
					name: operation.beneficiary.name,
					account: operation.beneficiary.account_number,
					id: operation.beneficiary?.id,
					...(operation.beneficiary.address && {
						address: {
							...operation.beneficiary.address,
							type: 'STRUCTURED',
						},
					}),
				},
				...(operation.ultimate_beneficiary && {
					ultimate_beneficiary: {
						name: operation.ultimate_beneficiary?.name,
						account: operation.ultimate_beneficiary?.account_number,
						id: operation.ultimate_beneficiary?.id,
						...(operation.ultimate_beneficiary.address && {
							address: {
								...operation.ultimate_beneficiary.address,
								type: 'STRUCTURED',
							},
						}),
					},
				}),
			}),
		},
	});
	const { setError, register, handleSubmit, formState, control, watch, setValue, reset } =
		sepaPaymentForm;
	const { t, i18n } = useTranslation();
	const fromAccountId = watch('account_uuid');
	const toAccount = useWatch({ name: 'beneficiarySelf', control });
	const documentNumber = useDocumentNumber(fromAccountId);
	const {
		data: createdTransaction,
		mutateAsync: createTransaction,
		isError: failedToCreateTransaction,
		isPending,
		error: submitError,
	} = useCreateTransaction();
	const {
		accounts,
		isLoading,
		pagination,
		isError: isFetchedWithErrors,
	} = usePaginatedAccounts({ isFull: true });

	const [toSelf, setToSelf] = useState(false);
	const [signingStatus, setSigningStatus] = useState<SigningStatus | undefined>();
	const geoData = useGeoDataContext();
	const statusMessageRef = useRef<HTMLDivElement>(null);
	const { mutate: createTemplate } = useCreateTemplate({ errorKey: 'failedAs' });
	const {
		validateIbanInput,
		validateBeneficiaryName,
		isIbanValidating,
		vop,
		isVopLoading,
		vopCheckFailed,
		vopRefetch,
	} = useSepaValidation({ watch });

	useEffect(() => {
		if (vopCheckFailed) {
			setFormStatus(FormStatus.WARNING);
		} else {
			setFormStatus(FormStatus.INITIAL);
		}
	}, [vopCheckFailed]);

	useEffect(() => {
		if (submitError?.response?.status !== 422) {
			return;
		}

		const errorsValidation = Object.entries(submitError?.response?.data?.errors ?? {});

		if (errorsValidation?.length > 0) {
			errorsValidation.forEach(([key, error]) => {
				if (key === 'iban') {
					setError('beneficiary.account', {
						message: error,
					});
					return;
				}
				if (key in ({} as SepaPayment)) {
					setError(key, {
						message: error,
					});
				}
			});
		}
	}, [setError, submitError?.response]);

	const errors = useMemo(() => formState.errors, [formState]);

	const onSubmit: SubmitHandler<SepaFormSchema> = (data) => {
		const {
			save_as_template,
			template_name,
			document_number,
			execution_date,
			beneficiarySelf: _beneficiarySelf,
			...restPaymentDetails
		} = data;

		const formData: SepaPayment = {
			...restPaymentDetails,
			document_number: document_number.toString(),
			execution_date,
			verification_of_payee_id: vop?.uuid,
		};

		createTransaction(
			{ data: formData },
			{
				onSuccess: (response) => {
					setFormStatus(FormStatus.CREATED);

					if (save_as_template && template_name) {
						createTemplate(
							prepareTemplate({
								template_name,
								...restPaymentDetails,
								payment_type: response.payment_type,
							}),
						);
					}
				},
			},
		);
	};

	const setSelfAsBeneficiary = () => {
		setToSelf(true);
		const account = fromAccountId
			? accounts.find((item) => item.uuid !== fromAccountId)
			: accounts?.[1];

		setValue('beneficiary.name', account?.ownerName ?? '', { shouldValidate: true });
		setValue('beneficiary.account', account?.iban ?? '', { shouldValidate: true });
		setValue('beneficiarySelf', account);
	};

	const clearBeneficiary = () => {
		setToSelf(false);
		setValue('beneficiary.name', '');
		setValue('beneficiary.account', '');
		setValue('beneficiarySelf', undefined);
	};

	const resetForm = (status: SigningStatus) => {
		reset(undefined, { keepDefaultValues: true, keepDirty: false });
		setSigningStatus(status);
		setToSelf(false);
		setFormStatus(FormStatus.INITIAL);
		documentNumber.refetch();
		vopRefetch();
		statusMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const setDefaultValue = (accountId: string) => {
		setValue('account_uuid', accountId);
	};

	return (
		<FormProvider {...sepaPaymentForm}>
			{isFetchedWithErrors && <Alert type="error">{t('common.failedFetching')}</Alert>}
			<>
				<form className="space-y-space-40" onSubmit={handleSubmit(onSubmit)}>
					<ControlledAccountList
						defaultValue={selectedAccount}
						disabledAccounts={[toAccount?.uuid ?? '']}
						label={t('payments.common.from')}
						name="account_uuid"
						setDefaultValue={selectedAccount?.uuid ? undefined : setDefaultValue}
					/>
					{toSelf && (
						<Controller
							name="beneficiarySelf"
							render={({ field: { ref, ...field } }) => {
								const handleChange = (account: Account) => {
									setValue('beneficiary.name', account.ownerName, {
										shouldValidate: true,
									});
									setValue('beneficiary.account', account.iban, { shouldValidate: true });

									field.onChange(account);
								};

								return (
									<ReceiverAccountRender
										ref={ref}
										accounts={accounts}
										disabledAccounts={fromAccountId ? [fromAccountId] : []}
										field={{ ...field, onChange: handleChange }}
										hideBalance
										isLoading={isLoading}
										pagination={pagination}
									/>
								);
							}}
						/>
					)}
					<div className="space-y-space-16">
						{/* pagination covers case what user have only one account */}
						{formStatus !== FormStatus.CREATED && pagination.totalCount > 1 && (
							<Button
								className="mt-4 border-neutral-100 focus-visible:rounded-sm"
								onClick={toSelf ? clearBeneficiary : setSelfAsBeneficiary}
								size="link"
								type="button"
								variant="link"
							>
								{toSelf ? t('payments.common.back') : t('payments.common.toMyAccount')}
							</Button>
						)}
						{Object.keys(formState.touchedFields).length === 0 && (
							<div ref={statusMessageRef}>
								{signingStatus === SigningStatus.ERROR && (
									<Alert type="error">{t('payments.common.orderSigningFailed')}</Alert>
								)}
								{signingStatus === SigningStatus.SUCCESS && (
									<Alert type="success">{t('payments.common.orderSigned')}</Alert>
								)}
								{signingStatus === SigningStatus.CANCELED && (
									<Alert type="warning">
										{t('payments.common.orderSigningCanceled')}
									</Alert>
								)}
							</div>
						)}
						<div>
							<ControlledTextInput
								disabled={formStatus === FormStatus.CREATED}
								isLoading={isVopLoading}
								label={t('payments.newPayment.form.name')}
								name="beneficiary.name"
								rules={{
									required: t('payments.newPayment.fieldIsRequired'),
									maxLength: {
										message: t('payments.common.maxLength', { maxLength: 70 }),
										value: 70,
									},
									validate: validateBeneficiaryName,
								}}
							/>
							{vop?.match_status && (
								<span
									className={clsx(
										'text-xs-medium',
										(vopStatusColor[vop.match_status] as string) ??
											'text-status-error-500',
									)}
								>
									{t(vopMessageKey[vop.match_status] as string, {
										fullName: vop.real_name,
									})}
								</span>
							)}
						</div>
						<ControlledTextInput
							disabled={formStatus === FormStatus.CREATED}
							isLoading={isIbanValidating}
							label={t('payments.newPayment.form.accountNo')}
							name="beneficiary.account"
							rules={{
								required: t('payments.newPayment.fieldIsRequired'),
								validate: validateIbanInput,
							}}
						/>
					</div>
					<div>
						<InputDropdown
							control={control}
							disableTyping={formStatus === FormStatus.CREATED}
							inputMode="decimal"
							inputRegister={register('amount', { required: true })}
							isError={!!errors?.amount || !!errors?.currency}
							isMandatory
							label={t('payments.newPayment.form.amount')}
							options={geoData?.data?.currencies ?? []}
							selectorRegister={register('currency', { required: true })}
						/>
						{(errors?.amount || errors?.currency) && (
							<Typography className="text-status-error-800" level="small-text">
								{errors?.amount?.message || errors?.currency?.message}
							</Typography>
						)}
					</div>
					<div className="space-y-space-16">
						<Typography level="h2">{t('payments.newPayment.data.title')}</Typography>
						<Typography level="h4">
							{t('payments.newPayment.data.transferType.label')}
						</Typography>
						<Controller
							control={control}
							name="payment_details"
							render={({ field }) => (
								<PaymentDetailsSwitcher<SepaPayment>
									{...field}
									error={errors.payment_details}
									isDisabled={formStatus === FormStatus.CREATED}
									required
								/>
							)}
							rules={{
								required: t('payments.newPayment.fieldIsRequired'),
								maxLength: {
									message: t('payments.common.maxLength', { maxLength: 140 }),
									value: 140,
								},
							}}
						/>
						<div>
							<Controller
								control={control}
								name={'execution_date'}
								render={({ field }) => (
									<DateStringPicker
										{...field}
										className="mt-space-16"
										disabledPast
										endMonth={addYears(today, 1)}
										error={errors.execution_date?.message}
										isDisabled={formStatus === FormStatus.CREATED}
										label={t('payments.newPayment.data.date')}
										locale={i18n.language === 'lt' ? lt : enUS}
										startDate={today}
										startMonth={new Date(2015, 1)}
									/>
								)}
								rules={{ required: t('payments.newPayment.fieldIsRequired') }}
							/>
							{errors.execution_date && (
								<Typography className="text-status-error-800" level="small-text">
									{errors?.execution_date.message}
								</Typography>
							)}
						</div>

						<DocumentNumberInput
							accountId={fromAccountId}
							disabled={formStatus === FormStatus.CREATED}
						/>
					</div>

					<Collapsible title={t('payments.newPayment.additionalData.button')}>
						<AdditionalDataForm formStatus={formStatus} />
					</Collapsible>

					<TemplateFromPayment disabled={formStatus === FormStatus.CREATED} />
					{failedToCreateTransaction ? (
						<Alert type={'error'}>{t('payments.common.orderFailed')}</Alert>
					) : (
						formStatus === FormStatus.CREATED && (
							<Alert type={'success'}>{t('payments.common.orderCreated')}</Alert>
						)
					)}

					<div>
						{formStatus === FormStatus.WARNING && vop?.uuid && (
							<VoPConfirmationBanner
								buttonText={t('vop.confirmLabel')}
								description={t('vop.warning.body')}
								id={vop.uuid}
								onConfirm={() => setFormStatus(FormStatus.INITIAL)}
								title={t('vop.warning.title')}
								type={AlertType.WARNING}
							/>
						)}
						{formStatus === FormStatus.INITIAL && (
							<Button
								className="mt-6"
								disabled={
									!formState?.isValid ||
									isIbanValidating ||
									isPending ||
									isVopLoading ||
									documentNumber.isLoading
								}
								type={'submit'}
							>
								{t('payments.common.createButton')}
							</Button>
						)}
					</div>
				</form>

				{formStatus === FormStatus.CREATED && (
					<PermissionGate capability={Capability.SIGN_PAYMENT}>
						<div style={{ marginTop: '14px' }}>
							<PaymentTfa onSign={resetForm} transactionId={createdTransaction?.id} />
						</div>
					</PermissionGate>
				)}
				<FormBlocker
					hasChanges={
						Object.keys(formState.dirtyFields).length > 0 &&
						formStatus !== FormStatus.CREATED
					}
				/>
			</>
		</FormProvider>
	);
};

SepaPaymentForm.displayName = 'SepaPaymentForm';
